'use client'

import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { isPopulatedObject } from '@/lib/utils'
import type { App, Comparison, ComparisonAppsApp } from '@/payload-types'

// Resolves the canonical stored comparison slug for a pair of apps (order-independent
// lookup, canonical order returned). Returns undefined when the pair has no stored
// comparison — callers then fall back to building an ad-hoc slug.
//
// Why this exists: compare links built client-side (e.g. the Best-list overflow menu)
// don't know a stored comparison's canonical app order. Emitting a non-canonical order
// (`b-vs-a` when canonical is `a-vs-b`) makes /compare/[slug] 307-redirect to the
// canonical URL. That redirect can't be prefetched and forces an on-demand render just
// to compute the redirect, so navigation is slow even though the target is pre-built.
// Resolving the canonical slug up front keeps the link pointed at the static page.
export type CompareSlugResolver = (appIdA: number, appIdB: number) => string | undefined

const CompareSlugContext = createContext<CompareSlugResolver | null>(null)

export function useOptionalCompareSlug(): CompareSlugResolver | null {
  return useContext(CompareSlugContext)
}

type CompareComparison = Pick<Comparison, 'id' | 'name' | 'comparisonApps' | 'tldr'>

// Takes the raw comparisons and builds the resolver client-side, so a SERVER
// component (e.g. the Best page) can supply serializable data instead of a function
// (functions can't cross the server→client boundary).
//
// DESIGN NOTE (evaluated 2026-06-23, kept intentionally — read before changing this):
//
// • Scope: this currently canonicalizes compare CTAs for the main app overview
//   list on /best/[category]. It does NOT cover earlier AppRanking rows or
//   BestSidebar; those need their own resolved hrefs if they must be canonical.
//
// • Not an LCP/TTFB lever by itself. `comparisons` is already fetched for
//   RelatedComparisons, so this provider adds no DB query; the map is built from
//   a page-scoped, capped comparison list and the useMemo cost is tiny. Removing
//   or reshaping this alone is not expected to move Lighthouse meaningfully. The
//   larger perf work is the depth-5 ACR graph, app-card client serialization,
//   below-fold fetchers, and PPR.
//
// • Why not a client/on-demand endpoint: a <Link> href must be known
//   synchronously at render for correct navigation and prefetch. Async client
//   resolution leaves a non-canonical href until it lands, so an early click
//   still pays the 307 this exists to avoid. Server-side resolution is fine; it
//   should be a local helper/map, not an HTTP endpoint.
//
// • Preferred cleanup: build the pair->slug map on the server and pass a compact
//   Record<string, string>, or pass resolved hrefs to small client islands if the
//   app cards move server-side. By itself this is mainly serialization/code-shape
//   cleanup, not the primary Lighthouse lever.
export function CompareSlugProvider({
  comparisons,
  children,
}: {
  comparisons: CompareComparison[]
  children: ReactNode
}) {
  const resolve = useMemo<CompareSlugResolver>(() => {
    const slugByPair = new Map<string, string>()
    for (const comparison of comparisons) {
      const comparisonApps = (
        (comparison.comparisonApps as { docs?: ComparisonAppsApp[] } | undefined)?.docs ?? []
      ).filter((ca): ca is ComparisonAppsApp => isPopulatedObject<App>(ca?.app))
      // The menu only links a single pair (row app vs top app); skip 3+ app comparisons.
      if (comparisonApps.length !== 2) continue
      const [first, second] = [...comparisonApps]
        .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity))
        .map((ca) => ca.app as App)
      if (!first?.slug || !second?.slug) continue
      const key = [first.id, second.id].sort((a, b) => a - b).join(':')
      slugByPair.set(key, `${first.slug}-vs-${second.slug}`)
    }
    return (appIdA, appIdB) => slugByPair.get([appIdA, appIdB].sort((a, b) => a - b).join(':'))
  }, [comparisons])

  return <CompareSlugContext.Provider value={resolve}>{children}</CompareSlugContext.Provider>
}
